import { DestroyRef, Service, computed, signal } from '@angular/core';

import { VERSION } from '../../../environments/version';

/** Cada cuánto se pregunta si hay una versión nueva publicada. */
const CADA_MS = 2 * 60_000;

/** La última versión que esta persona abrió: para avisarle una sola vez que cambió. */
const CLAVE_VISTA = 'faro.version.vista';

interface EnvJson {
  ENV?: string;
  APP_VERSION?: string;
}

/**
 * La versión de FARO: la que está corriendo y si hay una más nueva publicada.
 *
 * **De dónde sale.** La imagen de nginx escribe `assets/env.json` con el tag
 * (producción) o el commit (test) del build: ver `Dockerfile.*` y
 * `cloudbuild-*.yaml`. El bundle no lo sabe, porque se compila antes de que
 * exista el tag. Con `ng serve` ese archivo no está y queda `VERSION`.
 *
 * **Cómo se entera de una nueva.** `index.html` no se cachea y los bundles
 * llevan hash, así que una pestaña abierta sigue con el código viejo hasta
 * recargar. Cada `CADA_MS`, y al volver a la pestaña, se relee `env.json`: si la
 * versión cambió, `nueva` la trae y el encabezado ofrece recargar. No hay
 * service worker: no hace falta para esto y agrega una caché más que invalidar.
 *
 * Se usa `fetch` y no `HttpClient`: el archivo es del propio sitio y los
 * interceptores le pondrían la base de la API y el token.
 */
@Service()
export class VersionService {
  private readonly _actual = signal(VERSION);
  private readonly _nueva = signal<string | undefined>(undefined);

  /** La versión cargada en esta pestaña: `v1.2.0` en producción, el commit en test. */
  readonly actual = this._actual.asReadonly();
  /** Una versión publicada después de abrir la pestaña; `undefined` si no hay. */
  readonly nueva = this._nueva.asReadonly();
  /** Cómo se muestra: el tag con su `v`, el commit tal cual. */
  readonly etiqueta = computed(() => etiquetaDe(this._actual()));
  readonly etiquetaNueva = computed(() => {
    const nueva = this._nueva();
    return nueva ? etiquetaDe(nueva) : undefined;
  });

  /**
   * La versión que esta persona tenía la vez anterior, si cambió desde entonces
   * (para el aviso "FARO se actualizó"). Se lee una sola vez, en `iniciar`.
   */
  readonly anterior = signal<string | undefined>(undefined);

  private iniciado = false;

  /** Lee la versión publicada y arranca el sondeo. Una sola vez, al iniciar la app. */
  async iniciar(destroy: DestroyRef): Promise<void> {
    if (this.iniciado) return;
    this.iniciado = true;

    const publicada = await leerEnv();
    if (!publicada?.APP_VERSION) return; // `ng serve`: no hay nada que comparar.

    this._actual.set(publicada.APP_VERSION);
    this.recordarVista(publicada.APP_VERSION);

    const revisar = () => void this.revisar();
    const alVolver = () => {
      if (document.visibilityState === 'visible') revisar();
    };
    const intervalo = setInterval(revisar, CADA_MS);
    document.addEventListener('visibilitychange', alVolver);
    destroy.onDestroy(() => {
      clearInterval(intervalo);
      document.removeEventListener('visibilitychange', alVolver);
    });
  }

  /** Recarga la página para tomar la versión nueva. */
  actualizar(): void {
    location.reload();
  }

  private async revisar(): Promise<void> {
    // Ya se sabe que hay una: no hace falta seguir preguntando.
    if (this._nueva()) return;
    const publicada = await leerEnv();
    const version = publicada?.APP_VERSION;
    if (version && version !== this._actual()) this._nueva.set(version);
  }

  private recordarVista(version: string): void {
    try {
      const vista = localStorage.getItem(CLAVE_VISTA);
      if (vista && vista !== version) this.anterior.set(vista);
      localStorage.setItem(CLAVE_VISTA, version);
    } catch {
      /* sin almacenamiento: no se avisa del cambio, nada más */
    }
  }
}

/** `env.json`, sin caché; `undefined` si no está o no es JSON (con `ng serve` llega el `index.html`). */
async function leerEnv(): Promise<EnvJson | undefined> {
  try {
    const res = await fetch(`assets/env.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return undefined;
    return (await res.json()) as EnvJson;
  } catch {
    return undefined;
  }
}

/** `1.2.0` y `v1.2.0` → `v1.2.0`; un commit (`613add8`) queda igual. */
function etiquetaDe(version: string): string {
  return /^v?\d+\.\d+/.test(version) ? `v${version.replace(/^v/, '')}` : version;
}
